import { X, Plus, Trash2, Zap } from "lucide-react"
import type { NewConfigState, LoadBalancerURL, VendorIntake, Organization } from '@/features/fmo'
import { calculateTotalPercentage, isPercentageValid, autoConfigurePercentages, getAutoConfigurationMessage } from "../utils/validation"

interface CreateModalProps {
  isOpen: boolean
  newConfig: NewConfigState
  organizations: Organization[]
  vendorIntakes: VendorIntake[]
  onClose: () => void
  onCreate: () => void
  onConfigChange: (config: Partial<NewConfigState>) => void
  onUrlChange: (urlId: string, field: keyof LoadBalancerURL, value: any) => void
  onAddUrl: () => void
  onRemoveUrl: (urlId: string) => void
  onAutoConfigure: (urls: LoadBalancerURL[]) => void
}

export default function CreateModal({
  isOpen,
  newConfig,
  organizations,
  vendorIntakes,
  onClose,
  onCreate,
  onConfigChange,
  onUrlChange,
  onAddUrl,
  onRemoveUrl,
  onAutoConfigure
}: CreateModalProps) {
  if (!isOpen) return null

  const totalPercentage = calculateTotalPercentage(newConfig.assignedLinks)
  const isValid = isPercentageValid(newConfig.assignedLinks)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Create Load Balancer Configuration</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Organization</label>
            <select
              value={newConfig.organizationId}
              onChange={(e) => onConfigChange({ organizationId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Organization</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name} - {org.type}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-4">
            {newConfig.assignedLinks.map((url) => (
              <div key={url.id} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg">
                <div className="flex-1">
                  <select
                    value={url.linkId}
                    onChange={(e) => {
                      const vendorIntake = vendorIntakes.find(vi => vi.id === e.target.value)
                      onUrlChange(url.id, 'linkId', e.target.value)
                      onUrlChange(url.id, 'vendorIntake', vendorIntake)
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Vendor</option>
                    {vendorIntakes.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.vendorName} - {vendor.businessType}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="w-24">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={url.percentage}
                    onChange={(e) => onUrlChange(url.id, 'percentage', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <span className="text-sm text-gray-600 w-8">%</span>
                <button
                  onClick={() => onRemoveUrl(url.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))}
            
            <button
              onClick={onAddUrl}
              className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
            >
              <Plus className="w-5 h-5 mx-auto mb-2" />
              Add Vendor
            </button>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-blue-900">Total Percentage:</span>
              <span className={`font-bold ${isValid ? 'text-green-600' : 'text-red-600'}`}>
                {totalPercentage}%
              </span>
            </div>
            
            {newConfig.assignedLinks.length > 0 && (
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-blue-700">Auto-configure percentages:</span>
                <button
                  onClick={() => onAutoConfigure(autoConfigurePercentages(newConfig.assignedLinks))}
                  className="bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 text-sm"
                >
                  <Zap className="w-4 h-4" />
                  Auto-configure
                </button>
              </div>
            )}
            
            {newConfig.assignedLinks.length > 0 && (
              <p className="text-xs text-blue-600 mb-2">
                {getAutoConfigurationMessage(newConfig.assignedLinks.length)}
              </p>
            )}
            
            {!isValid && (
              <p className="text-sm text-red-600">
                Total percentage must equal exactly 100%
              </p>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onCreate}
            disabled={!newConfig.organizationId || !isValid}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create Configuration
          </button>
        </div>
      </div>
    </div>
  )
}
